import React, { useState, useCallback, useEffect, useRef } from 'react';
import { MindMapNode, Notebook, Language } from '../types';
import { X, GripVertical, Download, FileText, Globe, Archive, Eye, Sparkles, Loader2 } from 'lucide-react';
import { parseMarkdown } from '../utils/markdownUtils';
import { generateGuideIntro, generateGuideConclusion } from '../services/geminiService';

interface Props {
    notebook: Notebook;
    language: Language;
    onClose: () => void;
    initialSelectedIds?: string[];
}

interface GuideSection {
    nodeId: string;
    sectionHeader?: string; // optional header before this node
}

interface GuideData {
    title: string;
    subtitle: string;
    intro: string;
    conclusion: string;
    sections: GuideSection[];
    exportMode: 'archive' | 'commercial';
}

type Stage = 'select' | 'structure' | 'preview' | 'export';

function slugify(text: string) {
    return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

function nodeToMarkdown(node: MindMapNode, exportMode: 'archive' | 'commercial'): string {
    const lines: string[] = [];

    if (node.content?.trim()) {
        lines.push(node.content.trim());
        lines.push('');
    }

    if (node.analysis) {
        const a = node.analysis;
        if (a.keyInsights?.length) {
            lines.push('**Key Insights**');
            a.keyInsights.forEach(k => lines.push(`- ${k}`));
            lines.push('');
        }
        if (a.formulas?.length) {
            lines.push('**Formulas**');
            a.formulas.forEach(f => {
                lines.push(`$$${f.latex}$$`);
                if (f.meaning) lines.push(`*${f.meaning}*`);
            });
            lines.push('');
        }
        if (a.intuition) {
            lines.push(`**Intuition:** ${a.intuition}`);
            lines.push('');
        }
        if (a.commonMistakes?.length) {
            lines.push('**Common Mistakes**');
            a.commonMistakes.forEach(m => lines.push(`- ${m}`));
            lines.push('');
        }
    }

    // Include image only in archive mode
    if (exportMode === 'archive' && node.imageBase64) {
        lines.push(`![${node.title}](${node.imageBase64})`);
        lines.push('');
    }

    if (node.videoUrl) {
        lines.push(`📹 [Watch explanation](${node.videoUrl})`);
        lines.push('');
    }

    if (node.tags?.length) {
        lines.push(node.tags.join(' '));
        lines.push('');
    }

    return lines.join('\n');
}

function buildMarkdown(data: GuideData, nodes: MindMapNode[]): string {
    const lines: string[] = [];
    const nodeMap = new Map(nodes.map(n => [n.id, n]));

    lines.push(`# ${data.title}`);
    if (data.subtitle) lines.push(`*${data.subtitle}*`);
    lines.push('');
    lines.push('---');
    lines.push('');

    if (data.intro) {
        lines.push('## Introduction');
        lines.push(data.intro);
        lines.push('');
        lines.push('---');
        lines.push('');
    }

    // Table of contents
    lines.push('## Table of Contents');
    data.sections.forEach((s, i) => {
        const node = nodeMap.get(s.nodeId);
        if (node) lines.push(`${i + 1}. [${node.title || 'Untitled'}](#${slugify(node.title || 'untitled')})`);
    });
    lines.push('');
    lines.push('---');
    lines.push('');

    // Sections
    data.sections.forEach(s => {
        const node = nodeMap.get(s.nodeId);
        if (!node) return;
        if (s.sectionHeader) {
            lines.push(`## ${s.sectionHeader}`);
            lines.push('');
        }
        lines.push(`### ${node.title || 'Untitled'}`);
        lines.push('');
        lines.push(nodeToMarkdown(node, data.exportMode));
        lines.push('---');
        lines.push('');
    });

    if (data.conclusion) {
        lines.push('## Conclusion');
        lines.push(data.conclusion);
        lines.push('');
    }

    lines.push('---');
    lines.push(`*Created with [StudyBoard](https://studyboard.app)*`);

    return lines.join('\n');
}

export function GuideBuilderModal({ notebook, language, onClose, initialSelectedIds }: Props) {
    const vn = language === 'vietnamese';
    const nodes = notebook.nodes.filter(n => n.nodeType !== 'label');

    const hasInitial = initialSelectedIds && initialSelectedIds.length > 0;
    const [stage, setStage] = useState<Stage>(hasInitial ? 'structure' : 'select');
    const [selectedIds, setSelectedIds] = useState<string[]>(hasInitial ? initialSelectedIds : []);
    const [sections, setSections] = useState<GuideSection[]>(hasInitial ? initialSelectedIds.map(id => ({ nodeId: id })) : []);
    const [title, setTitle] = useState(notebook.title || 'My Study Guide');
    const [subtitle, setSubtitle] = useState('');
    const [intro, setIntro] = useState('');
    const [conclusion, setConclusion] = useState('');
    const [exportMode, setExportMode] = useState<'archive' | 'commercial'>('commercial');
    const [dragIdx, setDragIdx] = useState<number | null>(null);
    const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
    const [isGenIntro, setIsGenIntro] = useState(false);
    const [isGenConclusion, setIsGenConclusion] = useState(false);

    const nodeMap = new Map(nodes.map(n => [n.id, n]));

    const getSelectedNotes = () => sections
        .map(s => nodeMap.get(s.nodeId))
        .filter((n): n is MindMapNode => !!n)
        .map(n => ({ title: n.title || '', content: n.content || '' }));

    const handleGenIntro = async () => {
        setIsGenIntro(true);
        try {
            const text = await generateGuideIntro(title, getSelectedNotes(), language);
            setIntro(text);
        } catch (e) { console.error(e); } finally { setIsGenIntro(false); }
    };

    const handleGenConclusion = async () => {
        setIsGenConclusion(true);
        try {
            const text = await generateGuideConclusion(title, getSelectedNotes(), language);
            setConclusion(text);
        } catch (e) { console.error(e); } finally { setIsGenConclusion(false); }
    };

    const toggleNode = (id: string) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const goToStructure = () => {
        setSections(selectedIds.map(id => ({ nodeId: id })));
        setStage('structure');
    };

    const moveSection = (from: number, to: number) => {
        setSections(prev => {
            const next = [...prev];
            const [item] = next.splice(from, 1);
            next.splice(to, 0, item);
            return next;
        });
    };

    const updateSectionHeader = (idx: number, header: string) => {
        setSections(prev => prev.map((s, i) => i === idx ? { ...s, sectionHeader: header || undefined } : s));
    };

    const guideData: GuideData = { title, subtitle, intro, conclusion, sections, exportMode };

    const downloadMarkdown = () => {
        const md = buildMarkdown(guideData, nodes);
        const blob = new Blob([md], { type: 'text/markdown' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${slugify(title) || 'guide'}.md`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const buildGuideHtml = (forPrint = false) => {
        const nodeMap2 = new Map(nodes.map(n => [n.id, n]));

        const renderContent = (text: string): string => {
            // Split on double newlines → separate paragraphs; single newline → <br>
            return text.trim().split(/\n{2,}/).map(para =>
                `<p class="content">${parseMarkdown(para.trim().replace(/\n/g, '<br>'))}</p>`
            ).join('');
        };

        const renderNodeHtml = (node: MindMapNode): string => {
            const parts: string[] = [];
            // Screenshot at top, before content
            if (exportMode === 'archive' && node.imageBase64) {
                parts.push(`<img src="${node.imageBase64}" alt="${node.title}" class="node-img">`);
            }
            if (node.content?.trim()) {
                parts.push(renderContent(node.content));
            }
            if (node.analysis) {
                const a = node.analysis;
                if (a.keyInsights?.length) {
                    parts.push(`<div class="block"><strong>Key Insights</strong><ul>${a.keyInsights.map(k => `<li>${parseMarkdown(k)}</li>`).join('')}</ul></div>`);
                }
                if (a.formulas?.length) {
                    parts.push(`<div class="block"><strong>Formulas</strong>${a.formulas.map(f => `<div class="formula">${parseMarkdown(`$${f.latex}$`)}${f.meaning ? ` <em>— ${f.meaning}</em>` : ''}</div>`).join('')}</div>`);
                }
                if (a.intuition) {
                    parts.push(`<div class="block"><strong>Intuition:</strong> ${parseMarkdown(a.intuition)}</div>`);
                }
                if (a.commonMistakes?.length) {
                    parts.push(`<div class="block"><strong>Common Mistakes</strong><ul>${a.commonMistakes.map(m => `<li>${parseMarkdown(m)}</li>`).join('')}</ul></div>`);
                }
            }
            if (node.videoUrl) {
                parts.push(`<p class="video-link">📹 <a href="${node.videoUrl}">${node.videoUrl}</a></p>`);
            }
            if (node.tags?.length) {
                parts.push(`<p class="tags">${node.tags.join(' ')}</p>`);
            }
            return parts.join('\n');
        };

        const sectionsHtml = sections.map(s => {
            const node = nodeMap2.get(s.nodeId);
            if (!node) return '';
            return `
                ${s.sectionHeader ? `<h2 class="section-header">${s.sectionHeader}</h2>` : ''}
                <div class="section">
                    <h3>${node.title || 'Untitled'}</h3>
                    ${renderNodeHtml(node)}
                </div>`;
        }).join('\n');

        const tocHtml = sections.map((s, i) => {
            const node = nodeMap2.get(s.nodeId);
            return node ? `<li>${i + 1}. ${node.title || 'Untitled'}</li>` : '';
        }).join('');

        const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Inter', system-ui, sans-serif; font-size: 11pt; line-height: 1.7; color: #1a1a1a; background: white; }
  .page { max-width: 680px; margin: 0 auto; padding: ${forPrint ? '48px 56px' : '32px 40px'}; }
  h1 { font-size: ${forPrint ? '28pt' : '22pt'}; font-weight: 800; line-height: 1.2; margin-bottom: 8px; color: #111; }
  .subtitle { font-style: italic; color: #555; font-size: 13pt; margin-bottom: 28px; }
  .intro { font-size: 11pt; color: #333; margin-bottom: 28px; padding-bottom: 20px; border-bottom: 1px solid #ddd; }
  .toc { margin-bottom: 36px; padding: 16px 20px; background: #f8f8f8; border-left: 3px solid #8b5cf6; border-radius: 4px; }
  .toc h4 { font-size: 9pt; letter-spacing: 0.12em; text-transform: uppercase; color: #888; margin-bottom: 8px; }
  .toc ul { list-style: none; }
  .toc li { padding: 2px 0; font-size: 10pt; color: #444; }
  .section-header { font-size: 15pt; font-weight: 700; color: #6d28d9; margin: 36px 0 14px; padding-top: 28px; border-top: 2px solid #ede9fe; }
  .section { margin-bottom: 32px; padding-bottom: 24px; border-bottom: 1px solid #eee; }
  .section h3 { font-size: 13pt; font-weight: 700; color: #111; margin-bottom: 10px; }
  .content { margin-bottom: 16px; color: #222; line-height: 1.7; }
  .block { margin-bottom: 12px; }
  .block strong { display: block; font-size: 8.5pt; letter-spacing: 0.08em; text-transform: uppercase; color: #888; margin-bottom: 5px; }
  ul { padding-left: 20px; }
  li { margin-bottom: 4px; color: #333; }
  .formula { font-family: monospace; background: #f3f4f6; padding: 4px 8px; border-radius: 3px; margin: 4px 0; font-size: 10pt; }
  .node-img { max-width: 100%; border-radius: 6px; margin: 12px 0; border: 1px solid #e5e7eb; }
  .video-link { font-size: 10pt; color: #7c3aed; margin-bottom: 8px; }
  .video-link a { color: #7c3aed; }
  .tags { font-size: 9pt; color: #aaa; margin-top: 8px; }
  .footer { margin-top: 40px; padding-top: 14px; border-top: 1px solid #eee; text-align: center; font-size: 9pt; color: #bbb; }
  @media print { .page { padding: 16mm 20mm; max-width: 680px; } .section { page-break-inside: avoid; } .toc { break-after: avoid; } }`;

        const body = `<div class="page">
  <h1>${title}</h1>
  ${subtitle ? `<p class="subtitle">${subtitle}</p>` : ''}
  ${intro ? `<div class="intro">${intro.replace(/\n/g, '<br>')}</div>` : ''}
  <div class="toc"><h4>Table of Contents</h4><ul>${tocHtml}</ul></div>
  ${sectionsHtml}
  ${conclusion ? `<div class="section"><h3>Conclusion</h3><p class="content">${conclusion.replace(/\n/g, '<br>')}</p></div>` : ''}
  <div class="footer">Created with StudyBoard · studyboard.app</div>
</div>`;

        return { CSS, body };
    };

    const printGuide = () => {
        const { CSS, body } = buildGuideHtml(true);
        const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"><link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin><link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet"><title>${title}</title><style>${CSS}</style></head><body>${body}<script>document.fonts.ready.then(()=>window.print())<\/script></body></html>`;
        const win = window.open('', '_blank');
        if (!win) return;
        win.document.write(html);
        win.document.close();
    };

    const previewMarkdown = buildMarkdown(guideData, nodes);

    return (
        <>
            <div className="fixed inset-0 z-[400] bg-black/50 backdrop-blur-sm" onClick={onClose} />
            <div className="fixed z-[401] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-gray-100 dark:border-slate-700 w-[600px] max-w-[96vw] max-h-[90vh] flex flex-col animate-in fade-in zoom-in-95 duration-200">

                {/* Header */}
                <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 dark:border-slate-700 flex-shrink-0">
                    <div className="p-2 bg-violet-100 dark:bg-violet-900/30 rounded-xl">
                        <FileText size={18} className="text-violet-600 dark:text-violet-400" />
                    </div>
                    <div className="flex-1">
                        <h2 className="font-bold text-gray-900 dark:text-white text-base">Guide Builder</h2>
                        <p className="text-xs text-gray-400 mt-0.5 truncate">{notebook.title}</p>
                    </div>
                    {/* Stage tabs */}
                    <div className="flex items-center gap-0.5">
                        {(['select', 'structure', 'preview', 'export'] as Stage[]).map((s, i) => (
                            <button key={s} onClick={() => { if (s === 'structure' && selectedIds.length === 0) return; if (s === 'preview' || s === 'export') { setSections(prev => prev.length ? prev : selectedIds.map(id => ({ nodeId: id }))); } setStage(s); }}
                                className={`w-7 h-7 rounded-full text-xs font-bold transition-colors flex items-center justify-center ${stage === s ? 'bg-violet-500 text-white' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}
                                title={s === 'select' ? (vn ? 'Chọn' : 'Select') : s === 'structure' ? (vn ? 'Cấu trúc' : 'Structure') : s === 'preview' ? 'Preview' : 'Export'}>
                                {i + 1}
                            </button>
                        ))}
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-xl transition-colors ml-1">
                        <X size={16} className="text-gray-400" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto">

                    {/* ── Stage 1: Select nodes ── */}
                    {stage === 'select' && (
                        <div className="p-5 flex flex-col gap-3">
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                {vn ? `Chọn các note muốn đưa vào guide (${selectedIds.length} đã chọn)` : `Select notes to include in the guide (${selectedIds.length} selected)`}
                            </p>
                            <div className="flex gap-2">
                                <button onClick={() => setSelectedIds(nodes.map(n => n.id))} className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-800 text-gray-600 dark:text-gray-400 transition-colors">
                                    {vn ? 'Chọn tất cả' : 'Select all'}
                                </button>
                                <button onClick={() => setSelectedIds([])} className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-800 text-gray-600 dark:text-gray-400 transition-colors">
                                    {vn ? 'Bỏ hết' : 'Clear'}
                                </button>
                            </div>
                            <div className="flex flex-col gap-1.5 max-h-[50vh] overflow-y-auto pr-1">
                                {nodes.map(node => (
                                    <button key={node.id} onClick={() => toggleNode(node.id)}
                                        className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-all ${selectedIds.includes(node.id) ? 'border-violet-300 dark:border-violet-600 bg-violet-50 dark:bg-violet-900/20' : 'border-gray-100 dark:border-slate-700 hover:border-gray-200 dark:hover:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-800'}`}>
                                        <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${selectedIds.includes(node.id) ? 'border-violet-500 bg-violet-500' : 'border-gray-300 dark:border-slate-500'}`}>
                                            {selectedIds.includes(node.id) && <svg viewBox="0 0 12 10" fill="none" className="w-3 h-3"><path d="M1 5l3 3 7-7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{node.title || vn ? '(Chưa có tiêu đề)' : '(Untitled)'}</p>
                                            {node.content && <p className="text-xs text-gray-400 truncate mt-0.5">{node.content.slice(0, 80)}</p>}
                                        </div>
                                        {node.imageBase64 && <span className="text-[10px] text-amber-500 flex-shrink-0">📷</span>}
                                        {node.videoUrl && <span className="text-[10px] text-red-500 flex-shrink-0">▶</span>}
                                        {node.analysis && <span className="text-[10px] text-blue-500 flex-shrink-0">AI</span>}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* ── Stage 2: Structure ── */}
                    {stage === 'structure' && (
                        <div className="p-5 flex flex-col gap-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div className="col-span-2">
                                    <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1 block">{vn ? 'Tiêu đề guide' : 'Guide Title'}</label>
                                    <input value={title} onChange={e => setTitle(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-800 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-violet-300 dark:focus:ring-violet-700" />
                                </div>
                                <div className="col-span-2">
                                    <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1 block">{vn ? 'Phụ đề' : 'Subtitle'}</label>
                                    <input value={subtitle} onChange={e => setSubtitle(e.target.value)} placeholder={vn ? 'VD: Dựa trên hành trình học MIT 18.06' : 'e.g. Based on my MIT 18.06 learning journey'} className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-800 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-violet-300 dark:focus:ring-violet-700 placeholder-gray-300 dark:placeholder-slate-500" />
                                </div>
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{vn ? 'Lời mở đầu' : 'Introduction'}</label>
                                        <button onClick={handleGenIntro} disabled={isGenIntro} className="flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px] text-violet-600 dark:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors disabled:opacity-50">
                                            {isGenIntro ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
                                            AI
                                        </button>
                                    </div>
                                    <textarea value={intro} onChange={e => setIntro(e.target.value)} rows={3} className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-800 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-violet-300 dark:focus:ring-violet-700 resize-none" />
                                </div>
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{vn ? 'Kết luận' : 'Conclusion'}</label>
                                        <button onClick={handleGenConclusion} disabled={isGenConclusion} className="flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px] text-violet-600 dark:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors disabled:opacity-50">
                                            {isGenConclusion ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
                                            AI
                                        </button>
                                    </div>
                                    <textarea value={conclusion} onChange={e => setConclusion(e.target.value)} rows={3} className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-800 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-violet-300 dark:focus:ring-violet-700 resize-none" />
                                </div>
                            </div>

                            <div>
                                <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 block">
                                    {vn ? 'Sắp xếp thứ tự (kéo để đổi)' : 'Order sections (drag to reorder)'}
                                </label>
                                <div className="flex flex-col gap-1.5">
                                    {sections.map((s, i) => {
                                        const node = nodeMap.get(s.nodeId);
                                        if (!node) return null;
                                        return (
                                            <div key={s.nodeId}
                                                draggable
                                                onDragStart={() => setDragIdx(i)}
                                                onDragOver={(e) => { e.preventDefault(); setDragOverIdx(i); }}
                                                onDrop={() => { if (dragIdx !== null) moveSection(dragIdx, i); setDragIdx(null); setDragOverIdx(null); }}
                                                onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
                                                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all cursor-grab active:cursor-grabbing ${dragOverIdx === i ? 'border-violet-400 bg-violet-50 dark:bg-violet-900/20' : 'border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800'}`}>
                                                <GripVertical size={14} className="text-gray-300 dark:text-slate-500 flex-shrink-0" />
                                                <span className="text-xs text-gray-400 w-5 flex-shrink-0">{i + 1}</span>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate">{node.title || '(Untitled)'}</p>
                                                </div>
                                                <input
                                                    value={s.sectionHeader || ''}
                                                    onChange={e => updateSectionHeader(i, e.target.value)}
                                                    onClick={e => e.stopPropagation()}
                                                    placeholder={vn ? 'Header phần (tuỳ chọn)' : 'Section header (optional)'}
                                                    className="w-36 px-2 py-1 text-xs rounded-lg border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-violet-300 placeholder-gray-300 dark:placeholder-slate-500"
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Stage 3: Preview ── */}
                    {stage === 'preview' && (() => {
                        const { CSS, body } = buildGuideHtml(false);
                        return (
                            <div className="overflow-y-auto max-h-[62vh] bg-white rounded-b-2xl">
                                <style>{CSS}</style>
                                <div dangerouslySetInnerHTML={{ __html: body }} />
                            </div>
                        );
                    })()}

                    {/* ── Stage 4: Export ── */}
                    {stage === 'export' && (
                        <div className="p-5 flex flex-col gap-4">
                            {/* Export mode */}
                            <div>
                                <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 block">{vn ? 'Chế độ export' : 'Export Mode'}</label>
                                <div className="grid grid-cols-2 gap-3">
                                    <button onClick={() => setExportMode('commercial')}
                                        className={`flex flex-col items-start gap-1.5 px-4 py-3 rounded-xl border-2 transition-all text-left ${exportMode === 'commercial' ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20' : 'border-gray-200 dark:border-slate-600 hover:border-violet-200'}`}>
                                        <div className="flex items-center gap-2">
                                            <Globe size={14} className={exportMode === 'commercial' ? 'text-violet-600' : 'text-gray-400'} />
                                            <span className={`text-sm font-bold ${exportMode === 'commercial' ? 'text-violet-700 dark:text-violet-300' : 'text-gray-600 dark:text-gray-300'}`}>
                                                {vn ? 'Thương mại' : 'Commercial'}
                                            </span>
                                        </div>
                                        <p className="text-[11px] text-gray-400">{vn ? 'Không có screenshot nguồn. An toàn bản quyền.' : 'No source screenshots. Copyright-safe.'}</p>
                                    </button>
                                    <button onClick={() => setExportMode('archive')}
                                        className={`flex flex-col items-start gap-1.5 px-4 py-3 rounded-xl border-2 transition-all text-left ${exportMode === 'archive' ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20' : 'border-gray-200 dark:border-slate-600 hover:border-amber-200'}`}>
                                        <div className="flex items-center gap-2">
                                            <Archive size={14} className={exportMode === 'archive' ? 'text-amber-600' : 'text-gray-400'} />
                                            <span className={`text-sm font-bold ${exportMode === 'archive' ? 'text-amber-700 dark:text-amber-300' : 'text-gray-600 dark:text-gray-300'}`}>
                                                {vn ? 'Lưu trữ' : 'Archive'}
                                            </span>
                                        </div>
                                        <p className="text-[11px] text-gray-400">{vn ? 'Bao gồm screenshot. Chỉ dùng cá nhân.' : 'Includes screenshots. Personal use only.'}</p>
                                    </button>
                                </div>
                            </div>

                            {/* Summary */}
                            <div className="px-4 py-3 bg-gray-50 dark:bg-slate-800 rounded-xl border border-gray-100 dark:border-slate-700 text-sm text-gray-600 dark:text-gray-300 flex flex-col gap-1">
                                <span>📄 <strong>{sections.length}</strong> {vn ? 'notes' : 'notes'}</span>
                                <span>📝 <strong>{title}</strong></span>
                                <span>{exportMode === 'commercial' ? '🌐' : '📦'} {exportMode === 'commercial' ? (vn ? 'Commercial — không có source screenshot' : 'Commercial — no source screenshots') : (vn ? 'Archive — có source screenshot' : 'Archive — includes source screenshots')}</span>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <button onClick={downloadMarkdown}
                                    className="py-3 rounded-xl bg-gray-100 hover:bg-gray-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-200 text-sm font-bold transition-colors flex items-center justify-center gap-2">
                                    <Download size={15} />
                                    Markdown
                                </button>
                                <button onClick={printGuide}
                                    className="py-3 rounded-xl bg-violet-500 hover:bg-violet-600 text-white text-sm font-bold transition-colors flex items-center justify-center gap-2">
                                    <Download size={15} />
                                    PDF
                                </button>
                            </div>
                            <p className="text-xs text-gray-400 text-center">{vn ? 'PDF: trình duyệt mở tab mới → Ctrl+P → Save as PDF' : 'PDF: browser opens new tab → Ctrl+P → Save as PDF'}</p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-100 dark:border-slate-700 flex-shrink-0 flex gap-2">
                    {stage === 'select' && (
                        <button onClick={goToStructure} disabled={selectedIds.length === 0}
                            className="ml-auto px-5 py-2.5 rounded-xl bg-violet-500 hover:bg-violet-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-bold transition-colors">
                            {vn ? `Tiếp tục với ${selectedIds.length} note →` : `Continue with ${selectedIds.length} notes →`}
                        </button>
                    )}
                    {stage === 'structure' && (
                        <>
                            <button onClick={() => setStage('select')} className="px-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 text-gray-600 dark:text-gray-400 text-sm font-bold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors">←</button>
                            <button onClick={() => setStage('preview')} className="px-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 text-sm font-bold hover:bg-gray-50 dark:hover:bg-slate-800 text-gray-600 dark:text-gray-400 transition-colors flex items-center gap-2">
                                <Eye size={14} /> Preview
                            </button>
                            <button onClick={() => setStage('export')} className="ml-auto px-5 py-2.5 rounded-xl bg-violet-500 hover:bg-violet-600 text-white text-sm font-bold transition-colors">
                                {vn ? 'Export →' : 'Export →'}
                            </button>
                        </>
                    )}
                    {stage === 'preview' && (
                        <>
                            <button onClick={() => setStage('structure')} className="px-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 text-gray-600 dark:text-gray-400 text-sm font-bold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors">←</button>
                            <button onClick={() => setStage('export')} className="ml-auto px-5 py-2.5 rounded-xl bg-violet-500 hover:bg-violet-600 text-white text-sm font-bold transition-colors">
                                {vn ? 'Export →' : 'Export →'}
                            </button>
                        </>
                    )}
                    {stage === 'export' && (
                        <button onClick={() => setStage('structure')} className="px-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 text-gray-600 dark:text-gray-400 text-sm font-bold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors">← {vn ? 'Sửa' : 'Edit'}</button>
                    )}
                </div>
            </div>
        </>
    );
}
